import { Button, Divider, Input, ToggleButtonGroup } from '@healthinal/ui';
import type { FocusEvent } from 'react';
import type { Control, UseFormGetFieldState, UseFormRegister, UseFormWatch } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import {
  TuberculosisHistoryDto,
  TuberculosisHistoryDtoBcgVaccination,
  TuberculosisHistoryDtoImmunosuppressionStatus,
  TuberculosisHistoryDtoPreviousBloodTest,
  TuberculosisHistoryDtoPreviousBloodTestResult,
  TuberculosisHistoryDtoPreviousLatentTuberculosisTreatment,
  TuberculosisHistoryDtoPreviousTherapy,
  TuberculosisHistoryDtoPreviousTherapySuccessful,
  TuberculosisHistoryDtoPreviousTuberculinTest,
  TuberculosisHistoryDtoPreviousTuberculosis,
} from '../../../../api/generated/model';
import { SingeLineFormGrid, SingleLineFormControl } from '../../../../components/form/SingeLineFormComponents.tsx';
import { FieldMetaInfos } from '../../../../hooks/useAutoSaveForm.ts';
import { stringToNumberOrUndefined } from '../../../../utils/number.ts';
import { getStringOrUndefined } from '../../../../utils/string.ts';
import {
  getTuberculosisHistoryBloodTestResultName,
  getTuberculosisHistoryStatusName,
} from '../../../../utils/tuberculosisHistory.ts';

export interface TuberculosisHistoryFormValues {
  immunosuppressionStatus: TuberculosisHistoryDtoImmunosuppressionStatus;
  immunosuppressionDetails: string;
  typicalSymptoms: string;
  typicalSymptomsStarted: string;
  previousLatentTuberculosisTreatment: TuberculosisHistoryDtoPreviousLatentTuberculosisTreatment;
  previousLatentTuberculosisTreatmentDate: string;
  bcgVaccination: TuberculosisHistoryDtoBcgVaccination;
  bcgVaccinationDate: string;
  previousTuberculosis: TuberculosisHistoryDtoPreviousTuberculosis;
  previousTuberculosisDate: string;
  previousTherapy: TuberculosisHistoryDtoPreviousTherapy;
  previousTherapySuccessful: TuberculosisHistoryDtoPreviousTherapySuccessful | null;
  previousTherapyDate: string;
  previousTuberculinTest: TuberculosisHistoryDtoPreviousTuberculinTest;
  previousTuberculinTestFinding: string;
  previousTuberculinTestDate: string;
  previousBloodTest: TuberculosisHistoryDtoPreviousBloodTest;
  previousBloodTestResult: TuberculosisHistoryDtoPreviousBloodTestResult | null;
  previousBloodTestDate: string;
  previousTuberculosisInfection: TuberculosisHistoryDtoPreviousTuberculosis;
  previousTuberculosisInfectionDate: string;
}

export function getTuberculosisHistoryDefaultValues(
  tuberculosisHistory: TuberculosisHistoryDto,
): TuberculosisHistoryFormValues {
  return {
    immunosuppressionStatus: tuberculosisHistory.immunosuppressionStatus,
    immunosuppressionDetails: tuberculosisHistory.immunosuppressionDetails ?? '',
    typicalSymptoms: tuberculosisHistory.typicalSymptoms ?? '',
    typicalSymptomsStarted: tuberculosisHistory.typicalSymptomsStarted ?? '',
    previousLatentTuberculosisTreatment: tuberculosisHistory.previousLatentTuberculosisTreatment,
    previousLatentTuberculosisTreatmentDate: tuberculosisHistory.previousLatentTuberculosisTreatmentDate ?? '',
    bcgVaccination: tuberculosisHistory.bcgVaccination,
    bcgVaccinationDate: tuberculosisHistory.bcgVaccinationDate ?? '',
    previousTuberculosis: tuberculosisHistory.previousTuberculosis,
    previousTuberculosisDate: tuberculosisHistory.previousTuberculosisDate ?? '',
    previousTherapy: tuberculosisHistory.previousTherapy,
    previousTherapySuccessful: tuberculosisHistory.previousTherapySuccessful ?? null,
    previousTherapyDate: tuberculosisHistory.previousTherapyDate ?? '',
    previousTuberculinTest: tuberculosisHistory.previousTuberculinTest,
    previousTuberculinTestFinding: tuberculosisHistory.previousTuberculinTestFinding?.toString() ?? '',
    previousTuberculinTestDate: tuberculosisHistory.previousTuberculinTestDate ?? '',
    previousBloodTest: tuberculosisHistory.previousBloodTest,
    previousBloodTestResult: tuberculosisHistory.previousBloodTestResult ?? null,
    previousBloodTestDate: tuberculosisHistory.previousBloodTestDate ?? '',
    previousTuberculosisInfection: tuberculosisHistory.previousTuberculosisInfection,
    previousTuberculosisInfectionDate: tuberculosisHistory.previousTuberculosisInfectionDate ?? '',
  };
}

export function mapTuberculosisHistoryFormValuesToPayload(
  data: TuberculosisHistoryFormValues,
  { id, patientId }: { id: string; patientId: string },
): TuberculosisHistoryDto {
  return {
    id,
    patientId,
    immunosuppressionStatus: data.immunosuppressionStatus,
    immunosuppressionDetails: data.immunosuppressionDetails,
    typicalSymptoms: getStringOrUndefined(data.typicalSymptoms),
    typicalSymptomsStarted: getStringOrUndefined(data.typicalSymptomsStarted),
    previousLatentTuberculosisTreatment: data.previousLatentTuberculosisTreatment,
    previousLatentTuberculosisTreatmentDate: getStringOrUndefined(data.previousLatentTuberculosisTreatmentDate),
    bcgVaccination: data.bcgVaccination,
    bcgVaccinationDate: getStringOrUndefined(data.bcgVaccinationDate),
    previousTuberculosis: data.previousTuberculosis,
    previousTuberculosisDate: getStringOrUndefined(data.previousTuberculosisDate),
    previousTherapy: data.previousTherapy,
    previousTherapySuccessful: data.previousTherapySuccessful ?? undefined,
    previousTherapyDate: getStringOrUndefined(data.previousTherapyDate),
    previousTuberculinTest: data.previousTuberculinTest,
    previousTuberculinTestFinding: data.previousTuberculinTestFinding
      ? stringToNumberOrUndefined(data.previousTuberculinTestFinding)
      : undefined,
    previousTuberculinTestDate: getStringOrUndefined(data.previousTuberculinTestDate),
    previousBloodTest: data.previousBloodTest,
    previousBloodTestResult: data.previousBloodTestResult ?? undefined,
    previousBloodTestDate: getStringOrUndefined(data.previousBloodTestDate),
    previousTuberculosisInfection: data.previousTuberculosisInfection,
    previousTuberculosisInfectionDate: getStringOrUndefined(data.previousTuberculosisInfectionDate),
  };
}

export const tuberculosisHistoryFieldMetaInfos: FieldMetaInfos<TuberculosisHistoryFormValues> = {
  immunosuppressionStatus: { debounce: false },
  immunosuppressionDetails: { debounce: true },
  typicalSymptoms: { debounce: true },
  typicalSymptomsStarted: { debounce: true },
  previousLatentTuberculosisTreatment: { debounce: false },
  previousLatentTuberculosisTreatmentDate: { debounce: true },
  bcgVaccination: { debounce: false },
  bcgVaccinationDate: { debounce: true },
  previousTuberculosis: { debounce: false },
  previousTuberculosisDate: { debounce: true },
  previousTherapy: { debounce: false },
  previousTherapySuccessful: { debounce: false },
  previousTherapyDate: { debounce: true },
  previousTuberculinTest: { debounce: false },
  previousTuberculinTestFinding: { debounce: true },
  previousTuberculinTestDate: { debounce: true },
  previousBloodTest: { debounce: false },
  previousBloodTestResult: { debounce: false },
  previousBloodTestDate: { debounce: true },
  previousTuberculosisInfection: { debounce: false },
  previousTuberculosisInfectionDate: { debounce: true },
};

interface TuberculosisHistoryFormFieldsProps {
  registerWithSaveOnBlur: UseFormRegister<TuberculosisHistoryFormValues>;
  control: Control<TuberculosisHistoryFormValues>;
  watch: UseFormWatch<TuberculosisHistoryFormValues>;
  getFieldState: UseFormGetFieldState<TuberculosisHistoryFormValues>;
  handleSaveOnBlur: (onBlur: () => void) => void;
}

export function TuberculosisHistoryFormFields({
  registerWithSaveOnBlur,
  control,
  watch,
  getFieldState,
  handleSaveOnBlur,
}: TuberculosisHistoryFormFieldsProps) {
  const immunosuppressionStatusValue = watch('immunosuppressionStatus');
  const typicalSymptomsValue = watch('typicalSymptoms');
  const previousLatentTuberculosisTreatmentValue = watch('previousLatentTuberculosisTreatment');
  const bcgVaccinationValue = watch('bcgVaccination');
  const previousTuberculosisValue = watch('previousTuberculosis');
  const previousTherapyValue = watch('previousTherapy');
  const previousTuberculinTestsValue = watch('previousTuberculinTest');
  const previousBloodTestValue = watch('previousBloodTest');
  const previousTuberculosisInfectionValue = watch('previousTuberculosisInfection');

  const handleToggleButtonGroupBlur = (onBlur: () => void) => (event: FocusEvent<HTMLElement>) => {
    if (event.currentTarget.contains(event.relatedTarget)) {
      return;
    }
    handleSaveOnBlur(onBlur);
  };

  return (
    <SingeLineFormGrid>
      <SingleLineFormControl label={'Immunsupprimiert'} fieldState={getFieldState('immunosuppressionStatus')}>
        <Controller
          name={'immunosuppressionStatus'}
          control={control}
          render={({ field: { onBlur, onChange, value } }) => (
            <ToggleButtonGroup value={value} spacing={1} onBlur={handleToggleButtonGroupBlur(onBlur)}>
              {Object.values(TuberculosisHistoryDtoImmunosuppressionStatus).map((status) => (
                <Button value={status} key={status} onClick={() => onChange(status)}>
                  {getTuberculosisHistoryStatusName(status)}
                </Button>
              ))}
            </ToggleButtonGroup>
          )}
        />
      </SingleLineFormControl>
      {immunosuppressionStatusValue === TuberculosisHistoryDtoImmunosuppressionStatus.YES && (
        <SingleLineFormControl label={'Details'} fieldState={getFieldState('immunosuppressionDetails')}>
          <Input {...registerWithSaveOnBlur('immunosuppressionDetails')} variant={'plain'} />
        </SingleLineFormControl>
      )}
      <Divider />
      <SingleLineFormControl label={'TB-Symptome'} fieldState={getFieldState('typicalSymptoms')}>
        <Input {...registerWithSaveOnBlur('typicalSymptoms')} variant={'plain'} />
      </SingleLineFormControl>
      {typicalSymptomsValue && (
        <SingleLineFormControl label={'Symptome seit'} fieldState={getFieldState('typicalSymptomsStarted')}>
          <Input {...registerWithSaveOnBlur('typicalSymptomsStarted')} variant={'plain'} />
        </SingleLineFormControl>
      )}
      <Divider />
      <SingleLineFormControl label={'Frühere TB'} fieldState={getFieldState('previousTuberculosis')}>
        <Controller
          name={'previousTuberculosis'}
          control={control}
          render={({ field: { onBlur, onChange, value } }) => (
            <ToggleButtonGroup value={value} spacing={1} onBlur={handleToggleButtonGroupBlur(onBlur)}>
              {Object.values(TuberculosisHistoryDtoPreviousTuberculosis).map((status) => (
                <Button value={status} key={status} onClick={() => onChange(status)}>
                  {getTuberculosisHistoryStatusName(status)}
                </Button>
              ))}
            </ToggleButtonGroup>
          )}
        />
      </SingleLineFormControl>
      {previousTuberculosisValue === TuberculosisHistoryDtoPreviousTuberculosis.YES && (
        <SingleLineFormControl label={'Datum TB'} fieldState={getFieldState('previousTuberculosisDate')}>
          <Input {...registerWithSaveOnBlur('previousTuberculosisDate')} variant={'plain'} />
        </SingleLineFormControl>
      )}
      <Divider />
      <SingleLineFormControl label={'Frühere TB-Therapie'} fieldState={getFieldState('previousTherapy')}>
        <Controller
          name={'previousTherapy'}
          control={control}
          render={({ field: { onBlur, onChange, value } }) => (
            <ToggleButtonGroup value={value} spacing={1} onBlur={handleToggleButtonGroupBlur(onBlur)}>
              {Object.values(TuberculosisHistoryDtoPreviousTherapy).map((status) => (
                <Button value={status} key={status} onClick={() => onChange(status)}>
                  {getTuberculosisHistoryStatusName(status)}
                </Button>
              ))}
            </ToggleButtonGroup>
          )}
        />
      </SingleLineFormControl>
      {previousTherapyValue === TuberculosisHistoryDtoPreviousTherapy.YES && (
        <>
          <SingleLineFormControl label={'Therapiedatum'} fieldState={getFieldState('previousTherapyDate')}>
            <Input {...registerWithSaveOnBlur('previousTherapyDate')} variant={'plain'} />
          </SingleLineFormControl>
          <SingleLineFormControl
            label={'Therapie erfolgreich abgeschlossen'}
            fieldState={getFieldState('previousTherapySuccessful')}>
            <Controller
              name={'previousTherapySuccessful'}
              control={control}
              render={({ field: { onBlur, onChange, value } }) => (
                <ToggleButtonGroup value={value} spacing={1} onBlur={handleToggleButtonGroupBlur(onBlur)}>
                  {Object.values(TuberculosisHistoryDtoPreviousTherapySuccessful).map((status) => (
                    <Button value={status} key={status} onClick={() => onChange(status)}>
                      {getTuberculosisHistoryStatusName(status)}
                    </Button>
                  ))}
                </ToggleButtonGroup>
              )}
            />
          </SingleLineFormControl>
        </>
      )}
      <Divider />
      <SingleLineFormControl label={'Frühere TBI'} fieldState={getFieldState('previousTuberculosisInfection')}>
        <Controller
          name={'previousTuberculosisInfection'}
          control={control}
          render={({ field: { onBlur, onChange, value } }) => (
            <ToggleButtonGroup value={value} spacing={1} onBlur={handleToggleButtonGroupBlur(onBlur)}>
              {Object.values(TuberculosisHistoryDtoPreviousLatentTuberculosisTreatment).map((status) => (
                <Button value={status} key={status} onClick={() => onChange(status)}>
                  {getTuberculosisHistoryStatusName(status)}
                </Button>
              ))}
            </ToggleButtonGroup>
          )}
        />
      </SingleLineFormControl>
      {previousTuberculosisInfectionValue === TuberculosisHistoryDtoPreviousLatentTuberculosisTreatment.YES && (
        <SingleLineFormControl
          label={'Infektionsdatum'}
          fieldState={getFieldState('previousTuberculosisInfectionDate')}>
          <Input {...registerWithSaveOnBlur('previousTuberculosisInfectionDate')} variant={'plain'} />
        </SingleLineFormControl>
      )}
      <Divider />
      <SingleLineFormControl
        label={'Frühere TBI-Therapie'}
        fieldState={getFieldState('previousLatentTuberculosisTreatment')}>
        <Controller
          name={'previousLatentTuberculosisTreatment'}
          control={control}
          render={({ field: { onBlur, onChange, value } }) => (
            <ToggleButtonGroup value={value} spacing={1} onBlur={handleToggleButtonGroupBlur(onBlur)}>
              {Object.values(TuberculosisHistoryDtoPreviousLatentTuberculosisTreatment).map((status) => (
                <Button value={status} key={status} onClick={() => onChange(status)}>
                  {getTuberculosisHistoryStatusName(status)}
                </Button>
              ))}
            </ToggleButtonGroup>
          )}
        />
      </SingleLineFormControl>
      {previousLatentTuberculosisTreatmentValue === TuberculosisHistoryDtoPreviousLatentTuberculosisTreatment.YES && (
        <SingleLineFormControl
          label={'Behandlungsdatum'}
          fieldState={getFieldState('previousLatentTuberculosisTreatmentDate')}>
          <Input {...registerWithSaveOnBlur('previousLatentTuberculosisTreatmentDate')} variant={'plain'} />
        </SingleLineFormControl>
      )}
      <Divider />
      <SingleLineFormControl label={'Früherer Tuberkulintest'} fieldState={getFieldState('previousTuberculinTest')}>
        <Controller
          name={'previousTuberculinTest'}
          control={control}
          render={({ field: { onBlur, onChange, value } }) => (
            <ToggleButtonGroup value={value} spacing={1} onBlur={handleToggleButtonGroupBlur(onBlur)}>
              {Object.values(TuberculosisHistoryDtoPreviousTuberculinTest).map((status) => (
                <Button value={status} key={status} onClick={() => onChange(status)}>
                  {getTuberculosisHistoryStatusName(status)}
                </Button>
              ))}
            </ToggleButtonGroup>
          )}
        />
      </SingleLineFormControl>
      {previousTuberculinTestsValue === TuberculosisHistoryDtoPreviousTuberculinTest.YES && (
        <SingleLineFormControl label={'Testdatum'} fieldState={getFieldState('previousTuberculinTestDate')}>
          <Input {...registerWithSaveOnBlur('previousTuberculinTestDate')} variant={'plain'} />
        </SingleLineFormControl>
      )}
      {previousTuberculinTestsValue === TuberculosisHistoryDtoPreviousTuberculinTest.YES && (
        <SingleLineFormControl label={'Befund (mm)'} fieldState={getFieldState('previousTuberculinTestFinding')}>
          <Input type={'number'} {...registerWithSaveOnBlur('previousTuberculinTestFinding')} variant={'plain'} />
        </SingleLineFormControl>
      )}
      <Divider />
      <SingleLineFormControl label={'Früherer Bluttest'} fieldState={getFieldState('previousBloodTest')}>
        <Controller
          name={'previousBloodTest'}
          control={control}
          render={({ field: { onBlur, onChange, value } }) => (
            <ToggleButtonGroup value={value} spacing={1} onBlur={handleToggleButtonGroupBlur(onBlur)}>
              {Object.values(TuberculosisHistoryDtoPreviousBloodTest).map((status) => (
                <Button value={status} key={status} onClick={() => onChange(status)}>
                  {getTuberculosisHistoryStatusName(status)}
                </Button>
              ))}
            </ToggleButtonGroup>
          )}
        />
      </SingleLineFormControl>
      {previousBloodTestValue === TuberculosisHistoryDtoPreviousBloodTest.YES && (
        <>
          <SingleLineFormControl label={'Testdatum'} fieldState={getFieldState('previousBloodTestDate')}>
            <Input {...registerWithSaveOnBlur('previousBloodTestDate')} variant={'plain'} />
          </SingleLineFormControl>
          <SingleLineFormControl label={'Resultat'} fieldState={getFieldState('previousBloodTestResult')}>
            <Controller
              name={'previousBloodTestResult'}
              control={control}
              render={({ field: { onBlur, onChange, value } }) => (
                <ToggleButtonGroup value={value} spacing={1} onBlur={handleToggleButtonGroupBlur(onBlur)}>
                  {Object.values(TuberculosisHistoryDtoPreviousBloodTestResult).map((result) => (
                    <Button value={result} key={result} onClick={() => onChange(result)}>
                      {getTuberculosisHistoryBloodTestResultName(result)}
                    </Button>
                  ))}
                </ToggleButtonGroup>
              )}
            />
          </SingleLineFormControl>
        </>
      )}
      <Divider />
      <SingleLineFormControl label={'BCG Impfung'} fieldState={getFieldState('bcgVaccination')}>
        <Controller
          name={'bcgVaccination'}
          control={control}
          render={({ field: { onBlur, onChange, value } }) => (
            <ToggleButtonGroup value={value} spacing={1} onBlur={handleToggleButtonGroupBlur(onBlur)}>
              {Object.values(TuberculosisHistoryDtoBcgVaccination).map((status) => (
                <Button value={status} key={status} onClick={() => onChange(status)}>
                  {getTuberculosisHistoryStatusName(status)}
                </Button>
              ))}
            </ToggleButtonGroup>
          )}
        />
      </SingleLineFormControl>
      {bcgVaccinationValue === TuberculosisHistoryDtoBcgVaccination.YES && (
        <SingleLineFormControl label={'Impfdatum'} fieldState={getFieldState('bcgVaccinationDate')}>
          <Input {...registerWithSaveOnBlur('bcgVaccinationDate')} variant={'plain'} />
        </SingleLineFormControl>
      )}
    </SingeLineFormGrid>
  );
}
